import { useQuery } from '@tanstack/react-query';
import { deArrowThumbnailUrl, type VisiousSettings } from '@visious/core';
import { fetchDeArrowBranding } from '../api/dearrow';
import { getPlatform } from '../platform/instance';
import { useSettingsSlice } from '../platform/settings';

/** What a card swaps in — each half `null` where the record's own field stands. */
export interface DeArrowDisplay {
  readonly title: string | null;
  readonly thumbnailUrl: string | null;
}

const NOTHING: DeArrowDisplay = { title: null, thumbnailUrl: null };

/**
 * A feed page mounts 40 cards at once, and 40 parallel lookups is a burst the official service
 * rate-limits: measured 7 of 40 refused (answers without CORS headers, so they read as network
 * failures) on the first full-page test. The cap smooths the burst into waves the service accepts
 * — and it protects SponsorBlock too, which shares the origin and the same rate limit.
 */
const MAX_CONCURRENT_LOOKUPS = 8;
let activeLookups = 0;
const waitingLookups: (() => void)[] = [];

async function withLookupSlot<T>(run: () => Promise<T>): Promise<T> {
  while (activeLookups >= MAX_CONCURRENT_LOOKUPS) {
    await new Promise<void>((resolve) => waitingLookups.push(resolve));
  }
  activeLookups += 1;
  try {
    return await run();
  } finally {
    activeLookups -= 1;
    waitingLookups.shift()?.();
  }
}

/**
 * The community's title and thumbnail for one video — DeArrow (2026-08-02), called by every
 * video card: listing grids, the recommendations rail, playlist entries and history rows. The
 * watch page's own title and the player deliberately stay out: the reader has already chosen the
 * video, and de-clickbaiting a page they are on rewrites what they clicked.
 *
 * One query per (service, video), like SponsorBlock's, 30 min stale — so a video that appears in
 * three grids in a row costs one lookup. That is the DeArrow extension's own request pattern (a
 * hashed bucket per video as it renders); the responses are ~17 KB, which is the price of the
 * k-anonymity. Settings come from the live subscription: turning the integration off must strip
 * the titles already on screen, SponsorBlock's own rule.
 *
 * Failure collapses to "nothing stands" — the card keeps the record's title, and there is no empty
 * state to render, exactly like a storyboard that did not arrive.
 */
/** Module-level so the branch is read the same way on every call. */
const selectDeArrow = (settings: VisiousSettings): VisiousSettings['deArrow'] => settings.deArrow;

export function useDeArrowBranding(videoId: string | null): DeArrowDisplay {
  // The *slice*, not the document: this hook runs once per card, and subscribing each of them to the
  // whole of settings is what made one cross-tab write quadratic (`platform/settings.ts`).
  const settings = useSettingsSlice(selectDeArrow);

  // `null` is a card with no video behind it (a dead playlist entry) — hooks cannot be called
  // conditionally, so the "nothing to look up" case is a disabled query rather than an early return.
  const query = useQuery({
    queryKey: ['dearrow', settings.apiUrl, videoId],
    enabled: settings.enabled && videoId !== null,
    retry: false,
    staleTime: 30 * 60 * 1000,
    queryFn: async () => {
      if (videoId === null) return null;
      const boot = await getPlatform();
      if (!boot.ok) return null;
      return withLookupSlot(async () => {
        const result = await fetchDeArrowBranding(boot.platform, settings, videoId);
        return result.ok ? result.value : null;
      });
    },
  });

  const branding = settings.enabled ? (query.data ?? null) : null;
  if (branding === null || videoId === null) return NOTHING;

  return {
    title: branding.title,
    thumbnailUrl:
      settings.replaceThumbnails && branding.thumbnailTimestampSec !== null
        ? deArrowThumbnailUrl(settings, videoId, branding.thumbnailTimestampSec)
        : null,
  };
}
